// Quotation progress (PSSR-3293): maps the API quotation status onto the three
// states shown by the progress component and the insurer cards.

export type InsurerProgressStatus = "ready" | "waiting" | "unavailable";

export const READY_STATUSES = ["Available"];
// On-Hold quotations are being handled manually, so they go straight to the slow state.
export const WAITING_STATUSES = ["New", "On-Hold"];
export const SLOW_ON_ARRIVAL_STATUSES = ["On-Hold"];
export const UNAVAILABLE_STATUSES = ["Rejected"];

// An insurer still waiting after this long switches to the slow-state copy.
export const SLOW_STATE_AFTER_MS = 60 * 1000;

export interface ChipLogo {
  src: string;
  // Display size in px, taken from the Figma chips (assets are exported at 3x)
  width?: number;
  height?: number;
}

// Chip logos exported from the Figma design, keyed by quotation code (or name
// where the code isn't known). The API logos are padded squares that render
// tiny and soft at chip size. Insurers not listed here use the API image.
const CHIP_LOGO_DIR = "assets/image/insurer-logos/";
export const CHIP_LOGO_OVERRIDES: Record<string, ChipLogo> = {
  Allianz: { src: CHIP_LOGO_DIR + "chip-allianz.png", width: 75, height: 19 },
  Chubb: { src: CHIP_LOGO_DIR + "chip-chubb.png", width: 74, height: 9 },
  TMIM: {
    src: CHIP_LOGO_DIR + "chip-tokio-marine.png",
    width: 100,
    height: 25
  },
  Takaful_Malaysia: {
    src: CHIP_LOGO_DIR + "chip-takaful-malaysia.png",
    width: 114,
    height: 19
  },
  Zurich: { src: CHIP_LOGO_DIR + "chip-zurich.png", width: 74, height: 19 },
  Zurich_Takaful: {
    src: CHIP_LOGO_DIR + "chip-zurich.png",
    width: 74,
    height: 19
  },
  Ikhlas: {
    src: CHIP_LOGO_DIR + "chip-takaful-ikhlas.png",
    width: 26,
    height: 27
  },
  Sompo: {
    src: CHIP_LOGO_DIR + "chip-berjaya-sompo.png",
    width: 190,
    height: 21
  },
  Generali: { src: CHIP_LOGO_DIR + "chip-generali.png", width: 87, height: 17 }
};

export function getChipLogo(
  code: string,
  name: string,
  image: string
): ChipLogo {
  return (
    CHIP_LOGO_OVERRIDES[code] ?? CHIP_LOGO_OVERRIDES[name] ?? { src: image }
  );
}

export type QuotationProgressPhase = "loading" | "slow" | "finished";

export interface InsurerProgressItem {
  id: string;
  name: string;
  image: string;
  // Logo used in the progress chips (Figma export, or the API image)
  chipLogo: ChipLogo;
  code: string;
  status: InsurerProgressStatus;
  isSlow: boolean;
  // Position within the batch that turned Ready together, used to stagger the tick (null = no entrance)
  readyOrder: number | null;
}

export function toProgressStatus(
  apiStatus: string
): InsurerProgressStatus | null {
  if (READY_STATUSES.includes(apiStatus)) return "ready";
  if (WAITING_STATUSES.includes(apiStatus)) return "waiting";
  if (UNAVAILABLE_STATUSES.includes(apiStatus)) return "unavailable";
  return null;
}
